/**
 * Thumbnail Generation Utilities
 * Zero-dependency thumbnail generation using Canvas API
 */

export interface ThumbnailOptions {
  size?: number
  quality?: number
  format?: 'image/jpeg' | 'image/png' | 'image/webp'
  background?: string
  crop?: boolean
}

export interface ThumbnailResult {
  dataUrl: string
  blob: Blob
  width: number
  height: number
}

// Generate thumbnail from image file
export async function generateImageThumbnail(
  file: File,
  options: ThumbnailOptions = {}
): Promise<ThumbnailResult> {
  const {
    size = 120,
    quality = 0.8,
    format = 'image/jpeg',
    background = '#ffffff',
    crop = true
  } = options

  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          reject(new Error('Unable to get canvas context'))
          return
        }

        const { width, height } = calculateThumbnailDimensions(
          img.naturalWidth,
          img.naturalHeight,
          size,
          crop
        )

        canvas.width = width
        canvas.height = height

        // Fill background (useful for transparent images converted to JPEG)
        if (format === 'image/jpeg') {
          ctx.fillStyle = background
          ctx.fillRect(0, 0, width, height)
        }

        // Draw image
        if (crop) {
          drawImageCropped(ctx, img, width, height)
        } else {
          ctx.drawImage(img, 0, 0, width, height)
        }

        // Convert to blob
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to generate thumbnail blob'))
            return
          }

          resolve({
            dataUrl: canvas.toDataURL(format, quality),
            blob,
            width,
            height
          })
        }, format, quality)
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = () => {
      reject(new Error('Failed to load image for thumbnail generation'))
    }

    img.src = URL.createObjectURL(file)
  })
}

// Generate video thumbnail
export async function generateVideoThumbnail(
  file: File,
  options: ThumbnailOptions & { timeOffset?: number } = {}
): Promise<ThumbnailResult> {
  const {
    size = 120,
    quality = 0.8,
    format = 'image/jpeg',
    timeOffset = 1
  } = options

  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.muted = true
    video.playsInline = true

    const cleanup = () => {
      URL.revokeObjectURL(video.src)
    }

    video.onloadedmetadata = () => {
      // Set time offset (but not beyond video duration)
      video.currentTime = Math.min(timeOffset, video.duration || 0)
    }

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          cleanup()
          reject(new Error('Unable to get canvas context'))
          return
        }

        const { width, height } = calculateThumbnailDimensions(
          video.videoWidth,
          video.videoHeight,
          size,
          true
        )

        canvas.width = width
        canvas.height = height

        // Draw video frame
        drawImageCropped(ctx, video, width, height)

        canvas.toBlob((blob) => {
          cleanup()

          if (!blob) {
            reject(new Error('Failed to generate video thumbnail blob'))
            return
          }

          resolve({
            dataUrl: canvas.toDataURL(format, quality),
            blob,
            width,
            height
          })
        }, format, quality)
      } catch (error) {
        cleanup()
        reject(error)
      }
    }

    video.onerror = () => {
      cleanup()
      reject(new Error('Failed to load video for thumbnail generation'))
    }

    video.src = URL.createObjectURL(file)
  })
}

// Calculate thumbnail dimensions maintaining aspect ratio
function calculateThumbnailDimensions(
  originalWidth: number,
  originalHeight: number,
  maxSize: number,
  crop: boolean
): { width: number; height: number } {
  if (crop) {
    // Square crop
    return { width: maxSize, height: maxSize }
  }

  const aspectRatio = originalWidth / originalHeight
  let width: number
  let height: number

  if (aspectRatio > 1) {
    // Landscape
    width = Math.min(originalWidth, maxSize)
    height = width / aspectRatio
  } else {
    // Portrait or square
    height = Math.min(originalHeight, maxSize)
    width = height * aspectRatio
  }

  return {
    width: Math.round(width),
    height: Math.round(height)
  }
}

// Draw image cropped to center
function drawImageCropped(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement | HTMLVideoElement,
  canvasWidth: number,
  canvasHeight: number
): void {
  const imgWidth = 'naturalWidth' in img ? img.naturalWidth : img.videoWidth
  const imgHeight = 'naturalHeight' in img ? img.naturalHeight : img.videoHeight

  // Calculate crop dimensions to maintain aspect ratio
  const scale = Math.max(canvasWidth / imgWidth, canvasHeight / imgHeight)
  const scaledWidth = imgWidth * scale
  const scaledHeight = imgHeight * scale

  // Center the image
  const x = (canvasWidth - scaledWidth) / 2
  const y = (canvasHeight - scaledHeight) / 2

  ctx.drawImage(img, x, y, scaledWidth, scaledHeight)
}

// Generate fallback icon thumbnail for non-image files
export function generateIconThumbnail(
  fileName: string,
  fileType: string,
  options: ThumbnailOptions = {}
): ThumbnailResult {
  const {
    size = 120,
    quality = 0.8,
    format = 'image/png',
    background = '#f3f4f6'
  } = options

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!

  canvas.width = size
  canvas.height = size

  // Background
  ctx.fillStyle = background
  ctx.fillRect(0, 0, size, size)

  // File extension or icon
  const extension = getFileExtension(fileName)
  const icon = getFileTypeIcon(fileType)

  // Draw file icon
  ctx.fillStyle = getFileTypeColor(fileType)
  ctx.font = `${size * 0.3}px Arial`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  if (extension.length <= 4) {
    ctx.fillText(extension.toUpperCase(), size / 2, size / 2 - size * 0.1)
    ctx.font = `${size * 0.15}px Arial`
    ctx.fillStyle = '#6b7280'
    ctx.fillText(icon, size / 2, size / 2 + size * 0.15)
  } else {
    ctx.fillText(icon, size / 2, size / 2)
  }

  const dataUrl = canvas.toDataURL(format, quality)

  return new Promise<ThumbnailResult>((resolve) => {
    canvas.toBlob((blob) => {
      resolve({
        dataUrl,
        blob: blob!,
        width: size,
        height: size
      })
    }, format, quality)
  }) as any // Cast to sync return for fallback thumbnails
}

// Get file extension from filename
function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.')
  return lastDot > 0 ? filename.slice(lastDot + 1) : 'file'
}

// Get icon for file type
function getFileTypeIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return '🖼️'
  if (mimeType.startsWith('video/')) return '🎥'
  if (mimeType.startsWith('audio/')) return '🎵'
  if (mimeType.includes('pdf')) return '📄'
  if (mimeType.includes('zip') || mimeType.includes('archive')) return '📦'
  if (mimeType.includes('text')) return '📝'
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return '📊'
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return '📈'
  return '📎'
}

// Get color for file type
function getFileTypeColor(mimeType: string): string {
  if (mimeType.startsWith('image/')) return '#10b981'
  if (mimeType.startsWith('video/')) return '#3b82f6'
  if (mimeType.startsWith('audio/')) return '#8b5cf6'
  if (mimeType.includes('pdf')) return '#ef4444'
  if (mimeType.includes('zip') || mimeType.includes('archive')) return '#f59e0b'
  if (mimeType.includes('text')) return '#6b7280'
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return '#10b981'
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return '#f97316'
  return '#6b7280'
}

// Cache for generated thumbnails
const thumbnailCache = new Map<string, ThumbnailResult>()

// Generate thumbnail with caching
export async function generateThumbnailCached(
  file: File,
  options: ThumbnailOptions = {}
): Promise<ThumbnailResult> {
  const cacheKey = `${file.name}-${file.size}-${file.lastModified}-${JSON.stringify(options)}`

  if (thumbnailCache.has(cacheKey)) {
    return thumbnailCache.get(cacheKey)!
  }

  let thumbnail: ThumbnailResult

  if (file.type.startsWith('image/')) {
    thumbnail = await generateImageThumbnail(file, options)
  } else if (file.type.startsWith('video/')) {
    thumbnail = await generateVideoThumbnail(file, options)
  } else {
    thumbnail = generateIconThumbnail(file.name, file.type, options)
  }

  // Cache the result (with size limit to prevent memory leaks)
  if (thumbnailCache.size < 100) {
    thumbnailCache.set(cacheKey, thumbnail)
  }

  return thumbnail
}

// Clear thumbnail cache
export function clearThumbnailCache(): void {
  thumbnailCache.clear()
}

// Cleanup thumbnail resources
export function cleanupThumbnail(thumbnail: ThumbnailResult): void {
  try {
    URL.revokeObjectURL(thumbnail.dataUrl)
  } catch (error) {
    console.warn('Failed to cleanup thumbnail:', error)
  }
}